'use client';

import { c as _c } from "react/compiler-runtime";
import { useEffect, useEffectEvent } from 'react';
import { useLocalStorage } from 'usehooks-ts';
import { useIsPageVisible } from '@/hooks/useIsPageVisible';
import { verifyToken } from '@/services/verify-token';
const VERIFY_TOKEN_NEXT_POLL_AT_STORAGE_KEY = 'verify-token-poll-at.v1';
type UseVerifyTokenPollingParams = {
  authKey: string;
  isExternalIframe: boolean;
  isSignedIn: boolean;
  onLogoutRequired: () => void;
  pollIntervalMinutes: number;
};
const getJitteredDelayMs = (pollIntervalMinutes: number) => pollIntervalMinutes * 60_000 * (0.8 + Math.random() * 0.4);
export default function useVerifyTokenPolling(t0) {
  const $ = _c(21);
  const {
    authKey,
    isExternalIframe,
    isSignedIn,
    onLogoutRequired,
    pollIntervalMinutes
  } = t0;
  const [nextPollAt, setNextPollAt, removeNextPollAt] = useLocalStorage(VERIFY_TOKEN_NEXT_POLL_AT_STORAGE_KEY, null);
  const notifyLogoutRequired = useEffectEvent(onLogoutRequired);
  let t1;
  if ($[0] !== removeNextPollAt) {
    t1 = () => {
      try {
        removeNextPollAt();
      } catch {}
    };
    $[0] = removeNextPollAt;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const clearNextPollAt = useEffectEvent(t1);
  let t2;
  if ($[2] !== nextPollAt) {
    t2 = () => nextPollAt;
    $[2] = nextPollAt;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  const getNextPollAt = useEffectEvent(t2);
  const isPageVisible = useIsPageVisible();
  let t3;
  if ($[4] !== authKey || $[5] !== clearNextPollAt || $[6] !== getNextPollAt || $[7] !== isExternalIframe || $[8] !== isPageVisible || $[9] !== isSignedIn || $[10] !== notifyLogoutRequired || $[11] !== pollIntervalMinutes || $[12] !== setNextPollAt) {
    t3 = () => {
      if (!isSignedIn) {
        clearNextPollAt();
        return;
      }
      if (!authKey || isExternalIframe || !isPageVisible) {
        return;
      }
      let active = true;
      let timeoutId = null;
      let controller = null;
      const runPoll = async () => {
        timeoutId = null;
        controller = new AbortController();
        const outcome = await verifyToken(authKey, controller.signal);
        controller = null;
        if (!active) {
          return;
        }
        if (outcome === "logout_required") {
          clearNextPollAt();
          notifyLogoutRequired();
          return;
        }
        const delayMs = getJitteredDelayMs(pollIntervalMinutes);
        setNextPollAt(Date.now() + delayMs);
        timeoutId = setTimeout(runPoll, delayMs);
      };
      const storedNextPollAt = getNextPollAt();
      const hasStoredNextPollAt = typeof storedNextPollAt === "number" && Number.isFinite(storedNextPollAt) && storedNextPollAt > 0;
      const nextScheduledPollAt = hasStoredNextPollAt ? storedNextPollAt : Date.now() + getJitteredDelayMs(pollIntervalMinutes);
      if (!hasStoredNextPollAt) {
        setNextPollAt(nextScheduledPollAt);
      }
      const delayUntilNextPoll = Math.max(0, nextScheduledPollAt - Date.now());
      timeoutId = setTimeout(runPoll, delayUntilNextPoll);
      return () => {
        active = false;
        if (timeoutId) {
          clearTimeout(timeoutId);
        }
        controller?.abort();
      };
    };
    $[4] = authKey;
    $[5] = clearNextPollAt;
    $[6] = getNextPollAt;
    $[7] = isExternalIframe;
    $[8] = isPageVisible;
    $[9] = isSignedIn;
    $[10] = notifyLogoutRequired;
    $[11] = pollIntervalMinutes;
    $[12] = setNextPollAt;
    $[13] = t3;
  } else {
    t3 = $[13];
  }
  let t4;
  if ($[14] !== authKey || $[15] !== isExternalIframe || $[16] !== isPageVisible || $[17] !== isSignedIn || $[18] !== pollIntervalMinutes || $[19] !== setNextPollAt) {
    t4 = [authKey, isExternalIframe, isPageVisible, isSignedIn, pollIntervalMinutes, setNextPollAt];
    $[14] = authKey;
    $[15] = isExternalIframe;
    $[16] = isPageVisible;
    $[17] = isSignedIn;
    $[18] = pollIntervalMinutes;
    $[19] = setNextPollAt;
    $[20] = t4;
  } else {
    t4 = $[20];
  }
  useEffect(t3, t4);
}